/**
 * The isometric fleet on the board (scripts/import-iso-ships.ts): painted sprites per class,
 * laid into a square token box with the middle of the hull at (0.5, ART_KEEL), a grey mask of
 * the painted flags that the board tints in the player's colour, and a contact shadow.
 *
 * Views, as the isometric board shows a heading: `e` (bow to the bottom right of the screen),
 * `n` (bow to the top right), `ne` (in profile, bow to the right), `se` (bow towards the
 * viewer, straight down) and `nw` (stern towards the viewer, bow straight up). Mirrored they
 * give the other three headings.
 */
import type { ShipClassId, Side } from '@brig/rules-engine';
import manifest from '../assets/ships/iso/manifest.json';
import { SHIP_ART } from '../ships/art.ts';
import { HEADING_VECTOR, type Heading } from '../ships/sides.ts';
import { toView, toWorld, type Projection } from './projection.ts';

const files = import.meta.glob<string>('../assets/ships/iso/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

/** Whether ships are drawn with the isometric fleet (not in the vector fallback build). */
export const ISO_ART = SHIP_ART === 'source' && Object.keys(files).length > 0;

/**
 * A ship's drawn box against its cell: the longest ship spans most of its cell, with sea
 * around it between neighbours (the import lays the longest ship out at 90% of its box).
 */
export const ART_SCALE = 1;

/** Where the middle of the hull is, as a fraction of the drawn box down (the import's CENTRE). */
export const ART_KEEL = ISO_ART ? 0.72 : 27 / 32;

export type IsoView = 'e' | 'n' | 'ne' | 'se' | 'nw';
export type IsoSize = 256 | 512;
export type IsoLayer = 'hull' | 'flags' | 'band' | 'sails' | 'shadow';

interface Entry {
  readonly flags: boolean;
  /** Has its hull band and sails cut out to paint by side (scripts/paint-iso-ships.ts). */
  readonly painted?: boolean;
  readonly views: readonly string[];
}
const entries = manifest as Record<string, Entry | undefined>;

export function isoUrl(cls: ShipClassId, view: IsoView, size: IsoSize, layer: IsoLayer = 'hull'): string {
  const suffix = layer === 'hull' ? '' : `-${layer}`;
  const url = files[`../assets/ships/iso/${cls}-${view}${suffix}-${size}.webp`];
  if (!url) throw new Error(`missing isometric ship image for ${cls} (${view}${suffix})`);
  return url;
}

/** Whether a class has painted flags to fly in the player's colour. */
export function isoHasFlags(cls: ShipClassId): boolean {
  return entries[cls]?.flags ?? false;
}

/** Whether a class has its hull band and sails to paint by side (not the ironclads). */
export function isoPainted(cls: ShipClassId): boolean {
  return entries[cls]?.painted ?? false;
}

/**
 * The sails of each side (docs/ux.md, board marks): a warm cream for the first, a cool white
 * for the second, so two fleets differ at a glance even where their colours are close.
 */
export const SAIL_TONES: Readonly<Record<Side, string>> = { red: '#FFEED0', blue: '#E2ECFA' };

/** Whether a class has a view drawn (every class has `e`). */
export function hasView(cls: ShipClassId, view: IsoView): boolean {
  return view === 'e' || (entries[cls]?.views.includes(view) ?? false);
}

/** The way each view's bow points on screen (x right, y down), unmirrored. */
const VIEW_BOW: Readonly<Record<IsoView, { readonly x: number; readonly y: number }>> = {
  e: { x: 2, y: 1 },
  n: { x: 2, y: -1 },
  ne: { x: 1, y: 0 },
  se: { x: 0, y: 1 },
  nw: { x: 0, y: -1 },
};
const VIEWS = Object.keys(VIEW_BOW) as IsoView[];

const unit = (v: { x: number; y: number }) => {
  const n = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / n, y: v.y / n };
};

/**
 * Which drawn view shows a heading, and whether mirrored: the one whose bow points on screen
 * nearest to the way the heading does in this projection (on the isometric board each
 * heading has its own; on the flat chart, east is the profile). A class missing a view falls
 * back to the nearest it has.
 */
export function isoPose(
  cls: ShipClassId,
  heading: Heading,
  proj: Projection,
): { view: IsoView; mirror: boolean } {
  const h = HEADING_VECTOR[heading];
  const want = unit(toView(proj, h.x, h.y));
  let best = { view: 'e' as IsoView, mirror: false };
  let score = -Infinity;
  for (const view of VIEWS) {
    if (!hasView(cls, view)) continue;
    for (const mirror of [false, true]) {
      const b = unit(VIEW_BOW[view]);
      const dot = (mirror ? -b.x : b.x) * want.x + b.y * want.y;
      if (dot > score + 1e-9) {
        score = dot;
        best = { view, mirror };
      }
    }
  }
  return best;
}

/** A direction on the map, in world units (x east, y south), of unit length. */
export interface MapDir {
  readonly x: number;
  readonly y: number;
}

/**
 * Along which way on the map a ship's hull lies as drawn for a heading: the way its view's
 * bow points on screen, taken back down to the map. On the isometric board that is the
 * heading itself; elsewhere the nearest view may lie a little off it.
 */
export function hullDir(cls: ShipClassId, heading: Heading, proj: Projection): MapDir {
  const { view, mirror } = isoPose(cls, heading, proj);
  const b = VIEW_BOW[view];
  return unit(toWorld(proj, mirror ? -b.x : b.x, b.y));
}

/**
 * Two ships in a cell stand beam to beam: side by side across the way their hulls lie, the
 * second one nearer the viewer (south-east of the first).
 */
export function pairBeam(cls: ShipClassId, heading: Heading, proj: Projection): MapDir {
  const hull = hullDir(cls, heading, proj);
  const across = { x: -hull.y, y: hull.x };
  const ahead = across.x + across.y;
  const flip = ahead < -1e-9 || (Math.abs(ahead) <= 1e-9 && across.x < 0);
  return flip ? { x: -across.x, y: -across.y } : across;
}
